import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import CarePlanAdherenceBanner from '../components/CarePlanAdherenceBanner';
import MyPracticeCard from  '../components/provider/MyPracticeCard';


import {

  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  Avatar,
  LinearProgress,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Snackbar,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useAuth0 } from "../hooks/useAuth0";
import { apiService } from "../services/api";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SecurityIcon from "@mui/icons-material/Security";
import SettingsIcon from "@mui/icons-material/Settings";
import patientService from "../services/patientService";
import FlightIcon from "@mui/icons-material/Flight";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import ProfileCompletionChecklist from '../components/ProfileCompletionChecklist';
import ChangePasswordButton from '../components/ChangePasswordButton';
import EpicLabResults from '../components/EpicLabResults';
import ReminderSettingsCard from '../components/ReminderSettingsCard';
import ConnectEpicButton from '../components/ConnectEpicButton';

const ProfilePage = () => {
  const { user, logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    dateOfBirth: "",
    gender: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
    bloodType: "",
    heightCm: "",
    weightKg: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactRelationship: "",
    ckdStage: "",
    ckdEtiology: "",
    diagnosisDate: "",
    egfr: "",
    performanceStatus: "",
    dialysisModality: "",
    currentMedications: "",
    otherConditions: "",
    allergies: "",
    primaryPhysicianName: "",
    primaryPhysicianPhone: "",
    treatingInstitution: "",
    notes: "",
    willingToTravel: false,
    maxTravelDistanceKm: "",
    interestedInTrials: false,
    hipaaConsent: false,
    researchConsent: false,
    dataSharingConsent: false,
    marketingConsent: false,
    privacyPolicyAccepted: false,
    termsAccepted: false,
  });

  const [originalData, setOriginalData] = useState({});
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [patientId, setPatientId] = useState(null);

  const bloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const relationships = ["Spouse", "Partner", "Parent", "Child", "Sibling", "Friend", "Caregiver", "Other"];

  useEffect(() => {
    loadProfile();
  }, [user]);

  // Jump directly to a specific tab when navigated here with state, e.g.
  // the header's "Change Password" dropdown item linking straight to the
  // Account tab instead of always landing on Personal Info.
  useEffect(() => {
    if (location.state?.tab === 'account') {
      setActiveTab(4);
    }
  }, [location.state]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await patientService.getMyProfile();
      if (response.success && response.data) {
        const backendData = response.data;
        setProfileData(backendData);
        setOriginalData(backendData);
        setProfileCompletion(backendData.profileCompletionPercentage || 0);
        if (backendData.id) setPatientId(backendData.id);
      } else {
        showSnackbar("Failed to load profile", "error");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setPatientId("new");
      } else {
        showSnackbar("Error loading profile", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (!profileData.firstName || !profileData.lastName || !profileData.email) {
        showSnackbar("Please fill in all required fields", "error");
        setSaving(false);
        return;
      }
      if (!profileData.hipaaConsent) {
        showSnackbar("HIPAA Authorization is required", "error");
        setActiveTab(3);
        setSaving(false);
        return;
      }
      if (!profileData.privacyPolicyAccepted) {
        showSnackbar("Privacy Policy acceptance is required", "error");
        setActiveTab(3);
        setSaving(false);
        return;
      }
      if (!profileData.termsAccepted) {
        showSnackbar("Terms of Service acceptance is required", "error");
        setActiveTab(3);
        setSaving(false);
        return;
      }
      const dataToSave = { ...profileData };
      const response = patientId === "new"
        ? await patientService.createPatient(dataToSave)
        : await patientService.updatePatient(patientId, dataToSave);

      if (response.success) {
        const backendData = response.data;
        setProfileData(backendData);
        setOriginalData(backendData);
        setProfileCompletion(backendData.profileCompletionPercentage || 0);
        setEditMode(false);
        showSnackbar("Profile updated successfully!", "success");
        if (patientId === "new" && backendData?.id) setPatientId(backendData.id);
      } else {
        showSnackbar(response.message || "Failed to save profile", "error");
      }
    } catch (error) {
      showSnackbar("Error saving profile. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setProfileData(originalData);
    setEditMode(false);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeletingAccount(true);
    try {
      await apiService.deleteAccount();
      // Account and all data are gone server-side at this point — log out
      // locally and send to a plain landing page. No further app state
      // should be trusted or referenced after this succeeds.
      await logout({ logoutParams: { returnTo: window.location.origin } });
    } catch (error) {
      console.error('Account deletion failed:', error);
      setDeleteError(
        error.response?.data?.error || 'Something went wrong deleting your account. Please try again.'
      );
      setDeletingAccount(false);
    }
  };

  const genders = ["Male", "Female", "Other", "Prefer not to say"];
  const states = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
          <Typography variant="h6" sx={{ mt: 2, textAlign: "center" }}>
            Loading your profile...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar src={user?.picture} alt={user?.name} sx={{ width: 80, height: 80, border: "3px solid #1A5EA8" }} />
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight="bold" gutterBottom>My Profile</Typography>
            <Typography variant="body1" color="text.secondary">Manage your personal and medical information</Typography>
          </Grid>
          <Grid item>
            {!editMode ? (
              <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditMode(true)}
                sx={{ background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)" }}>
                Edit Profile
              </Button>
            ) : (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} disabled={saving}>Cancel</Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
                  sx={{ background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)" }}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item>
            <button onClick={() => navigate('/my-health/pathway')} style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
              background: 'linear-gradient(135deg, #1A5EA8, #0B2545)', color: 'white', border: 'none',
              borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              letterSpacing: '0.05em', textTransform: 'uppercase', fontFamily: "'DM Sans', system-ui, sans-serif",
              boxShadow: '0 4px 14px rgba(102,126,234,0.4)',
            }}>
              🗺️ VIEW TREATMENT PATHWAY
            </button>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={() => navigate("/my-health/find-provider")} startIcon={<LocalHospitalIcon />}
              sx={{ background: "linear-gradient(135deg,#1A5EA8,#0B2545)", color: "white", fontWeight: 700, fontSize: 15, padding: "12px 28px", borderRadius: 2, boxShadow: "0 4px 16px rgba(102,126,234,0.4)" }}>
              🏥 Find Provider Near Me
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ── Onboarding banner: only shown for brand-new, mostly-blank profiles ── */}
      {profileCompletion < 20 && (
        <Box
          sx={{
            mb: 3,
            p: 3,
            borderRadius: 3,
            background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              🏥 New here? Connect your health records to get started instantly
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              We'll pull in your name, contact info, diagnosis, and recent labs automatically — no manual typing needed.
              Prefer to fill it in yourself? Just continue below.
            </Typography>
          </Box>
          <ConnectEpicButton />
        </Box>
      )}

      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">Profile Completion</Typography>
            <Chip label={`${profileCompletion}%`}
              color={profileCompletion === 100 ? "success" : profileCompletion >= 50 ? "warning" : "error"}
              icon={profileCompletion === 100 ? <CheckCircleIcon /> : <WarningIcon />} />
          </Box>
          <LinearProgress variant="determinate" value={profileCompletion} sx={{
            height: 10, borderRadius: 5, backgroundColor: "rgba(102, 126, 234, 0.1)",
            "& .MuiLinearProgress-bar": { background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)" },
          }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Complete your profile to get better trial matches and personalized recommendations
          </Typography>
        </CardContent>
      </Card>

      {profileCompletion === 100 && (
        <Box sx={{ bgcolor: 'success.light', p: 2, mb: 3, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Profile Complete!</Typography>
              <Typography variant="body2" color="text.secondary">Ready to view your matched clinical trials</Typography>
            </Box>
          </Box>
          <Button variant="contained" color="success" onClick={() => navigate('/dashboard')}>View Matched Trials</Button>
        </Box>
      )}
      <CarePlanAdherenceBanner patientId={patientId} audience="patient" />
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper elevation={3}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="fullWidth" sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tab icon={<PersonIcon />} label="Personal Info" iconPosition="start" />
              <Tab icon={<LocalHospitalIcon />} label="Medical Info" iconPosition="start" />
              <Tab icon={<FlightIcon />} label="Trials" iconPosition="start" />
              <Tab icon={<SecurityIcon />} label="Consents" iconPosition="start" />
              <Tab icon={<SettingsIcon />} label="Account" iconPosition="start" />
            </Tabs>

            <Box sx={{ p: 4 }}>
              {activeTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sx={{ width: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ width: '100%' }}>Personal Information</Typography>
                    <Divider sx={{ width: '100%', mb: 3 }} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="First Name" required value={profileData.firstName}
                      onChange={(e) => handleInputChange("firstName", e.target.value)} disabled={!editMode} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Middle Name" value={profileData.middleName}
                      onChange={(e) => handleInputChange("middleName", e.target.value)} disabled={!editMode} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Last Name" required value={profileData.lastName}
                      onChange={(e) => handleInputChange("lastName", e.target.value)} disabled={!editMode} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Date of Birth" type="date" required value={profileData.dateOfBirth}
                      onChange={(e) => handleInputChange("dateOfBirth", e.target.value)} disabled={!editMode}
                      InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth disabled={!editMode}>
                      <InputLabel>Gender</InputLabel>
                      <Select value={profileData.gender} label="Gender" onChange={(e) => handleInputChange("gender", e.target.value)}>
                        {genders.map((gender) => <MenuItem key={gender} value={gender}>{gender}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth disabled={!editMode}>
                      <InputLabel>Blood Type</InputLabel>
                      <Select value={profileData.bloodType} label="Blood Type" onChange={(e) => handleInputChange("bloodType", e.target.value)}>
                        <MenuItem value=""><em>None</em></MenuItem>
                        {bloodTypes.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Email" type="email" required value={profileData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)} disabled={!editMode} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Phone Number" type="tel" value={profileData.phoneNumber}
                      onChange={(e) => handleInputChange("phone", e.target.value)} disabled={!editMode} />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField fullWidth label="Street Address" value={profileData.streetAddress}
                      onChange={(e) => handleInputChange("address", e.target.value)} disabled={!editMode} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="City" value={profileData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)} disabled={!editMode} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth disabled={!editMode}>
                      <InputLabel>State</InputLabel>
                      <Select value={profileData.state} label="State" onChange={(e) => handleInputChange("state", e.target.value)}>
                        {states.map((state) => <MenuItem key={state} value={state}>{state}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="ZIP Code" value={profileData.zipCode}
                      onChange={(e) => handleInputChange("zipCode", e.target.value)} disabled={!editMode} />
                  </Grid>

                  <Grid item xs={12} sx={{ width: '100%' }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Physical Measurements</Typography>
                    <Divider sx={{ mb: 3 }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Height (cm)" type="number" value={profileData.heightCm}
                      onChange={(e) => handleInputChange("heightCm", e.target.value)} disabled={!editMode}
                      helperText="Your height in centimeters" InputProps={{ inputProps: { min: 0, max: 300, step: 0.1 } }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Weight (kg)" type="number" value={profileData.weightKg}
                      onChange={(e) => handleInputChange("weightKg", e.target.value)} disabled={!editMode}
                      helperText="Your weight in kilograms" InputProps={{ inputProps: { min: 0, max: 500, step: 0.1 } }} />
                  </Grid>

                  <Grid item xs={12} sx={{ width: '100%' }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ContactPhoneIcon /> Emergency Contact
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Contact Name" value={profileData.emergencyContactName}
                      onChange={(e) => handleInputChange("emergencyContactName", e.target.value)} disabled={!editMode} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Contact Phone" type="tel" value={profileData.emergencyContactPhone}
                      onChange={(e) => handleInputChange("emergencyContactPhone", e.target.value)} disabled={!editMode} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth disabled={!editMode}>
                      <InputLabel>Relationship</InputLabel>
                      <Select value={profileData.emergencyContactRelationship} label="Relationship"
                        onChange={(e) => handleInputChange("emergencyContactRelationship", e.target.value)}>
                        <MenuItem value=""><em>None</em></MenuItem>
                        {relationships.map((rel) => <MenuItem key={rel} value={rel}>{rel}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {activeTab === 1 && (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sx={{ width: '100%' }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ width: '100%' }}>Diagnosis Information</Typography>
                      <Divider sx={{ width: '100%', mb: 3 }} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth label="CKD Stage *" value={profileData.ckdStage || ""}
                        onChange={(e) => handleInputChange("ckdStage", e.target.value)} disabled={!editMode} required select>
                        <MenuItem value="1">Stage 1</MenuItem>
                        <MenuItem value="2">Stage 2</MenuItem>
                        <MenuItem value="3a">Stage 3a</MenuItem>
                        <MenuItem value="3b">Stage 3b</MenuItem>
                        <MenuItem value="4">Stage 4</MenuItem>
                        <MenuItem value="5">Stage 5</MenuItem>
                        <MenuItem value="5D">Stage 5D (Dialysis-dependent)</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth label="CKD Etiology" value={profileData.ckdEtiology || ""}
                        onChange={(e) => handleInputChange("ckdEtiology", e.target.value)} disabled={!editMode} select>
                        <MenuItem value="">None / Not Specified</MenuItem>
                        <MenuItem value="Diabetic Nephropathy">Diabetic Nephropathy</MenuItem>
                        <MenuItem value="Hypertensive Nephrosclerosis">Hypertensive Nephrosclerosis</MenuItem>
                        <MenuItem value="Glomerulonephritis">Glomerulonephritis</MenuItem>
                        <MenuItem value="Polycystic Kidney Disease">Polycystic Kidney Disease</MenuItem>
                        <MenuItem value="Unknown/Other">Unknown/Other</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth label="Diagnosis Date *" type="date" value={profileData.diagnosisDate || ""}
                        onChange={(e) => handleInputChange("diagnosisDate", e.target.value)} disabled={!editMode} required
                        InputLabelProps={{ shrink: true }} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth label="eGFR (mL/min/1.73m²)" type="number" value={profileData.egfr || ""}
                        onChange={(e) => handleInputChange("egfr", e.target.value)} disabled={!editMode}
                        helperText="Estimated Glomerular Filtration Rate" />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth label="Performance Status (ECOG)" value={profileData.performanceStatus || ""}
                        onChange={(e) => handleInputChange("performanceStatus", e.target.value)} disabled={!editMode} select>
                        <MenuItem value="">Not Assessed</MenuItem>
                        <MenuItem value="0">ECOG 0 - Fully active</MenuItem>
                        <MenuItem value="1">ECOG 1 - Light work only</MenuItem>
                        <MenuItem value="2">ECOG 2 - Self-care, unable to work</MenuItem>
                        <MenuItem value="3">ECOG 3 - Limited self-care</MenuItem>
                        <MenuItem value="4">ECOG 4 - Completely disabled</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth label="Dialysis Modality *" value={profileData.dialysisModality || ""}
                        onChange={(e) => handleInputChange("dialysisModality", e.target.value)} disabled={!editMode} required select
                        helperText="Current kidney replacement therapy status">
                        <MenuItem value="Pre-dialysis">Pre-dialysis</MenuItem>
                        <MenuItem value="Hemodialysis">Hemodialysis</MenuItem>
                        <MenuItem value="Peritoneal Dialysis">Peritoneal Dialysis</MenuItem>
                        <MenuItem value="Transplant">Transplant</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} sx={{ width: '100%' }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ width: '100%' }}>Current Medications</Typography>
                      <Divider sx={{ width: '100%', mb: 3 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Current Medications" value={profileData.currentMedications || ""}
                        onChange={(e) => handleInputChange("currentMedications", e.target.value)} disabled={!editMode}
                        multiline rows={4}
                        placeholder="List your current medications (one per line)&#10;Example:&#10;Metformin 500mg twice daily&#10;Lisinopril 10mg once daily"
                        helperText="Include all medications, supplements, and treatments you are currently taking" />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} sx={{ width: '100%' }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ width: '100%' }}>Other Medical Conditions</Typography>
                      <Divider sx={{ width: '100%', mb: 3 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Other Medical Conditions" value={profileData.medicalConditions || ""}
                        onChange={(e) => handleInputChange("medicalConditions", e.target.value)} disabled={!editMode}
                        multiline rows={3}
                        placeholder="List any other medical conditions or comorbidities&#10;Example:&#10;Type 2 Diabetes&#10;Hypertension&#10;Chronic Kidney Disease"
                        helperText="Include any conditions that may affect treatment options" />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} sx={{ width: '100%' }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ width: '100%' }}>Allergies</Typography>
                      <Divider sx={{ width: '100%', mb: 3 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Drug & Other Allergies" value={profileData.allergies || ""}
                        onChange={(e) => handleInputChange("allergies", e.target.value)} disabled={!editMode}
                        multiline rows={3}
                        placeholder="List any allergies to medications, foods, or other substances&#10;Example:&#10;Penicillin - causes rash&#10;Shellfish - anaphylaxis&#10;Latex"
                        helperText="Include the reaction if known (rash, swelling, difficulty breathing, etc.)" />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} sx={{ width: '100%' }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ width: '100%' }}>Healthcare Providers</Typography>
                      <Divider sx={{ width: '100%', mb: 3 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Primary Physician Name" value={profileData.primaryPhysicianName || ""}
                        onChange={(e) => handleInputChange("primaryPhysicianName", e.target.value)} disabled={!editMode}
                        placeholder="Dr. John Smith" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Physician Phone" value={profileData.primaryPhysicianPhone || ""}
                        onChange={(e) => handleInputChange("primaryPhysicianPhone", e.target.value)} disabled={!editMode}
                        placeholder="(555) 123-4567" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Treating Institution / Hospital" value={profileData.treatingInstitution || ""}
                        onChange={(e) => handleInputChange("treatingInstitution", e.target.value)} disabled={!editMode}
                        placeholder="Mayo Clinic, Johns Hopkins, Cleveland Clinic, etc."
                        helperText="Name of the primary hospital or medical center treating your amyloidosis" />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} sx={{ width: '100%' }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ width: '100%' }}>Additional Notes</Typography>
                      <Divider sx={{ width: '100%', mb: 3 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Additional Medical Notes" value={profileData.notes || ""}
                        onChange={(e) => handleInputChange("notes", e.target.value)} disabled={!editMode}
                        multiline rows={4}
                        placeholder="Any additional information about your medical history, symptoms, or condition that may be relevant for clinical trial matching"
                        helperText="Include any other relevant medical information" />
                    </Grid>
                  </Grid>

                  {/* ========== LAB RESULTS FROM CONNECTED HEALTH RECORDS (EPIC) ========== */}
                  <EpicLabResults />

                  {/* ========== DAILY CHECK-IN REMINDERS ========== */}
                  <Box sx={{ mt: 3 }}>
                    <ReminderSettingsCard />
                  </Box>
                </Box>
              )}

              {activeTab === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sx={{ width: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Clinical Trial Preferences</Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Help us match you with the most relevant clinical trials.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={<Switch checked={profileData.interestedInTrials}
                            onChange={(e) => handleInputChange("interestedInTrials", e.target.checked)}
                            disabled={!editMode} color="primary" />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">Interested in Clinical Trials</Typography>
                              <Typography variant="body2" color="text.secondary">I want to be notified about trials matching my profile</Typography>
                            </Box>
                          } />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={<Switch checked={profileData.willingToTravel}
                            onChange={(e) => handleInputChange("willingToTravel", e.target.checked)}
                            disabled={!editMode} color="primary" />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">Willing to Travel</Typography>
                              <Typography variant="body2" color="text.secondary">I am willing to travel to participate in trials</Typography>
                            </Box>
                          } />
                      </CardContent>
                    </Card>
                  </Grid>

                  {profileData.willingToTravel && (
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Maximum Travel Distance (km)" type="number" value={profileData.maxTravelDistanceKm}
                        onChange={(e) => handleInputChange("maxTravelDistanceKm", e.target.value)} disabled={!editMode}
                        helperText="How far are you willing to travel?" InputProps={{ inputProps: { min: 0, max: 10000 } }} />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Note:</strong> Indicating interest does not commit you to participation.
                        We will only contact you about trials matching your profile.
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              )}

              {activeTab === 3 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Privacy & Consents</Typography>
                    <Divider sx={{ mb: 3 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={<Switch checked={profileData.hipaaConsent}
                            onChange={(e) => handleInputChange("hipaaConsent", e.target.checked)}
                            disabled={!editMode} color="primary" />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" color="error">HIPAA Authorization (Required) *</Typography>
                              <Typography variant="body2" color="text.secondary">
                                I authorize NephroTrax to use and disclose my protected health information for treatment coordination and clinical trial matching.
                              </Typography>
                            </Box>
                          } />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={<Switch checked={profileData.privacyPolicyAccepted}
                            onChange={(e) => handleInputChange("privacyPolicyAccepted", e.target.checked)}
                            disabled={!editMode} color="primary" />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" color="error">Privacy Policy (Required) *</Typography>
                              <Typography variant="body2" color="text.secondary">
                                I agree to the{" "}
                                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#1A5EA8", textDecoration: "underline" }}>Privacy Policy</a>{" "}
                                and understand how my personal and medical data will be collected, used, stored, and protected by NephroTrax.
                              </Typography>
                            </Box>
                          } />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={<Switch checked={profileData.termsAccepted}
                            onChange={(e) => handleInputChange("termsAccepted", e.target.checked)}
                            disabled={!editMode} color="primary" />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" color="error">Terms of Service (Required) *</Typography>
                              <Typography variant="body2" color="text.secondary">
                                I agree to the{" "}
                                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#1A5EA8", textDecoration: "underline" }}>Terms of Service</a>{" "}
                                and understand the conditions of using NephroTrax, including trial matching services and data usage.
                              </Typography>
                            </Box>
                          } />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={<Switch checked={profileData.researchConsent}
                            onChange={(e) => handleInputChange("researchConsent", e.target.checked)}
                            disabled={!editMode} color="primary" />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">Research Participation</Typography>
                              <Typography variant="body2" color="text.secondary">
                                I consent to being contacted about clinical research opportunities and trials that match my profile.
                              </Typography>
                            </Box>
                          } />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={<Switch checked={profileData.dataSharingConsent}
                            onChange={(e) => handleInputChange("dataSharingConsent", e.target.checked)}
                            disabled={!editMode} color="primary" />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">Data Sharing</Typography>
                              <Typography variant="body2" color="text.secondary">
                                I allow my de-identified data to be shared with research institutions to advance amyloidosis research.
                              </Typography>
                            </Box>
                          } />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={<Switch checked={profileData.marketingConsent}
                            onChange={(e) => handleInputChange("marketingConsent", e.target.checked)}
                            disabled={!editMode} color="primary" />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">Marketing Communications</Typography>
                              <Typography variant="body2" color="text.secondary">
                                I agree to receive newsletters, updates, and educational content about amyloidosis and clinical trials.
                              </Typography>
                            </Box>
                          } />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info">
                      You can update your consent preferences at any time. Your data privacy is our top priority.
                    </Alert>
                  </Grid>
                </Grid>
              )}

              {activeTab === 4 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sx={{ width: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Account Settings</Typography>
                    <Divider sx={{ mb: 3 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Email Address</Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>{user?.email}</Typography>
                        <Alert severity="info" sx={{ mt: 2 }}>Please contact support to change your email address.</Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <MyPracticeCard />
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderColor: "error.main" }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" color="error" gutterBottom>Danger Zone</Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Once you delete your account, there is no going back. Please be certain.
                        </Typography>
                        <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>
                          Delete Account
                        </Button>

                        <Dialog
                          open={deleteDialogOpen}
                          onClose={() => { if (!deletingAccount) { setDeleteDialogOpen(false); setDeleteConfirmText(''); setDeleteError(''); } }}
                          maxWidth="sm"
                          fullWidth
                        >
                          <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>
                            Delete Your Account
                          </DialogTitle>
                          <DialogContent>
                            <Alert severity="error" sx={{ mb: 2 }}>
                              This permanently deletes your account, profile, lab results, connected health
                              records, and subscription. This cannot be undone.
                            </Alert>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              Type <strong>DELETE</strong> below to confirm.
                            </Typography>
                            <TextField
                              fullWidth
                              autoFocus
                              value={deleteConfirmText}
                              onChange={(e) => setDeleteConfirmText(e.target.value)}
                              placeholder="DELETE"
                              disabled={deletingAccount}
                            />
                            {deleteError && (
                              <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>
                            )}
                          </DialogContent>
                          <DialogActions sx={{ px: 3, pb: 3 }}>
                            <Button
                              onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(''); setDeleteError(''); }}
                              disabled={deletingAccount}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                              onClick={handleDeleteAccount}
                              startIcon={deletingAccount ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                              {deletingAccount ? 'Deleting…' : 'Permanently Delete My Account'}
                            </Button>
                          </DialogActions>
                        </Dialog>
                        <section>
                          <h3>Security</h3>
                          <ChangePasswordButton />
                        </section>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Paper>

          <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfilePage;